import { htmlToPlain } from '@zaplify/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { MessageDto } from './dtos/message.dto';

dayjs.extend(relativeTime);

type ConversationMessage = Pick<
    MessageDto,
    'sentOn' | 'direction' | 'fullName' | 'subjectLine' | 'content' | 'actionType'
>;
// interface ConversationMessage {
//     sentOn: Date | undefined;
//     direction: string;
//     fullName: string | undefined;
//     subjectLine: string | undefined;
//     content: string | undefined;
//     actionType: string | undefined;
// }

export class PrettyConversation {
    public create(messages: ConversationMessage[]): ConversationShort {
        /** ordered ascending */
        const orderedMessages = messages
            .filter((message) => message.sentOn)
            .sort((a, b) => new Date(a.sentOn!).getTime() - new Date(b.sentOn!).getTime());

        return this.getFormattedConversation(orderedMessages);
    }

    private getFormattedConversation(messages: ConversationMessage[]): ConversationShort {
        const formattedMessages = messages.map((message) => this.getFormattedMessage(message));

        return new ConversationShort(formattedMessages);
    }

    public getFormattedMessage(message: ConversationMessage): IConversationMessageShort {
        let formattedDate: string | undefined;

        if (message.sentOn) {
            const date = new Date(message.sentOn);
            const weekday = date.toLocaleString('en-US', { weekday: 'long' });
            const month = date.toLocaleString('en-US', { month: 'long' }); // E.g. February
            const day = date.getDate();
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');

            formattedDate = `${weekday}, ${month} ${day} ${year} @ ${hours}:${minutes} (${dayjs(date).fromNow()})`;
        }

        return {
            actionType: message.actionType as string,
            date: formattedDate,
            sentBy: message.direction?.toLowerCase() === 'in' ? message.fullName || 'Other person' : 'ME',
            content: this.getFormattedMessageContent(message),
            direction: message.direction,
        };
    }

    private getFormattedMessageContent(message: ConversationMessage): { subjectLine?: string; content: string } {
        return message.subjectLine
            ? { subjectLine: message.subjectLine as string, content: message.content as string }
            : { content: message.content as string };
    }
}

interface IConversationMessageShort {
    actionType: string;
    date?: string;
    sentBy: string;
    content: { subjectLine?: string; content: string };
    direction: string;
}
export class ConversationShort {
    private messagesAsc: IConversationMessageShort[];
    constructor(messages: IConversationMessageShort[]) {
        if (!messages || messages.length === 0) {
            this.messagesAsc = [];
        } else {
            this.messagesAsc = [...messages].sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());
        }
    }

    private getShortMessages(messageLengthLimit: number): IConversationMessageShort[] {
        return this.messagesAsc.map((message) => {
            if (message?.content?.content?.length > messageLengthLimit) {
                message.content.content = message.content.content.slice(0, messageLengthLimit) + '...';
            }

            return message;
        });
    }
    getFormattedStringShort(params?: { nMessages?: number; maxStringLengthPerMessage?: number }): string {
        if (this.messagesAsc.length === 0) {
            return '';
        }
        const messageLengthLimit = params?.maxStringLengthPerMessage ?? 20_000;

        return this.getShortMessages(messageLengthLimit)
            .filter((message) => message.content)
            .slice(-(params?.nMessages ?? this.messagesAsc.length))
            .map((message) => `<Message>\n${this.formatMessage(message)}\n</Message>`)
            .join('\n\n');
    }

    private formatMessage(message: IConversationMessageShort): string {
        return (
            `${message.date} ${message.actionType?.toLowerCase()?.replace('_', ' ')} From: ${message.sentBy} ${
                message.direction === 'out' ? '' : '(CONTACT)'
            }` +
            '\n' +
            (message.content.subjectLine ? `Subject: ${message.content.subjectLine}\nContent: ` : '') +
            `${message.content.content}`
        );
    }

    getLastMessage(): IConversationMessageShort {
        return this.messagesAsc[this.messagesAsc.length - 1];
    }

    getLatestMessagesShort(n: number): string {
        return this.getShortMessages(20_000)
            .filter((message) => message.content)
            .slice(-n)
            .map((message) => `<Message>${this.formatMessage(message)}</Message>`)
            .join('\n');
    }
}
